import { useMemo } from "react";

import { startCase } from "lodash";
import { Controller, useForm } from "react-hook-form";

import { Button, Select, toastError, toastSuccess } from "@alpacahq/ui/old";

import { useGetIsLegalEntity, useMutationRequest } from "~/v2/api/hooks";
import { type OwnerDetails, patchDetails } from "~/v2/api/rest/account";
import { Field } from "~/v2/components/form";
import {
  getMinOrMaxAsRangeValue,
  getRangesAsOptionsArray,
} from "~/v2/utils/ranges";
import { FUNDING_SOURCES } from "../../entityOnboarding/constants";
import { FUNDING_SOURCE } from "../../onboarding/constants";
import type { SettingsTabProps } from "../index";

type FinancialProfile = {
  annual_income: string;
  total_net_worth: string;
  funding_source: string;
  liquid_net_worth: string;
};

export default ({ details, account }: SettingsTabProps) => {
  const { isLegalEntity } = useGetIsLegalEntity();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FinancialProfile>({
    mode: "all",
    defaultValues: {
      annual_income:
        details.annual_income_max != null
          ? getMinOrMaxAsRangeValue("annual_income", details.annual_income_max)
          : undefined,
      liquid_net_worth:
        details.liquid_net_worth_min != null
          ? getMinOrMaxAsRangeValue(
              "liquid_net_worth",
              details.liquid_net_worth_min,
            )
          : undefined,
      total_net_worth:
        details.total_net_worth_min != null
          ? getMinOrMaxAsRangeValue(
              "total_net_worth",
              details.total_net_worth_min,
            )
          : undefined,
      funding_source: details.funding_source?.at(0),
    },
  });

  const financialMutation = useMutationRequest(
    (payload: Partial<OwnerDetails>) => patchDetails(account.id, payload),
  );

  const fundingSources = useMemo(() => {
    return isLegalEntity
      ? FUNDING_SOURCES.map((s) => ({ value: s, label: startCase(s) }))
      : FUNDING_SOURCE;
  }, [isLegalEntity]);

  const onSubmit = (data: FinancialProfile) => {
    financialMutation.mutate(
      {
        funding_source: [data.funding_source],
        // parse ranges, they are stored as strings in the form
        ...JSON.parse(data.annual_income),
        ...JSON.parse(data.total_net_worth),
        ...JSON.parse(data.liquid_net_worth),
      },
      {
        onSuccess: () => {
          // show a success toast
          toastSuccess("Financial Profile updated");
        },
        onError: () => {
          // show an error toast
          toastError("Error updating financial profile");
        },
      },
    );
  };

  return (
    <div className="w-full max-w-[600px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col">
          <h2 className="mb-5 font-bold text-2xl">Financial Profile</h2>
          <div className="flex justify-between">
            <div className="mr-4 w-[50%]">
              <Field
                label="Annual Income"
                required={true}
                error={!!errors.annual_income}
                errorMsg="Required"
              >
                <Controller
                  name="annual_income"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      placeholder="None selected"
                      options={getRangesAsOptionsArray(field.name)}
                      {...field}
                    />
                  )}
                />
              </Field>
            </div>
            <div className="w-[50%]">
              <Field
                label="Estimated Net Worth"
                required={true}
                error={!!errors.total_net_worth}
                errorMsg="Required"
              >
                <Controller
                  name="total_net_worth"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      placeholder="None selected"
                      options={getRangesAsOptionsArray(field.name)}
                      {...field}
                    />
                  )}
                />
              </Field>
            </div>
          </div>
          <div className="mt-4 flex justify-between">
            <div className="mr-4 w-[50%]">
              <Field
                label="Invest-able / Liquid Assets"
                required={true}
                error={!!errors.liquid_net_worth}
                errorMsg="Required"
              >
                <Controller
                  name="liquid_net_worth"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      placeholder="None selected"
                      options={getRangesAsOptionsArray(field.name)}
                      {...field}
                    />
                  )}
                />
              </Field>
            </div>
            <div className="w-[50%]">
              <Field
                label="Account Funding Source"
                required={true}
                error={!!errors.funding_source}
                errorMsg="Required"
              >
                <Controller
                  name="funding_source"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      placeholder="None selected"
                      options={fundingSources}
                      {...field}
                    />
                  )}
                />
              </Field>
            </div>
          </div>
          <Button disabled={!isDirty} className="mt-8 mr-auto" type="submit">
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
};
